import { useCallback, useEffect, useState } from 'react';
import { addDays, getMonth, subMonths } from 'date-fns';
import { IntlShape } from 'react-intl';
import type { DateValue } from '../types';
import type { OnValidateCallbackType } from '../validation';
import { getNextState, isValidDate, type ActiveDateField } from '../utils/dateRange';
import { isDateInView } from '../utils/rangeDisplay';

export interface ValidState {
  from: string;
  to: string;
  type: string;
}

export interface DateRangeStateOptions {
  value: DateValue;
  onChange: (value: DateValue) => void;
  onCancel: () => void;
  today: Date;
  disabledDates: (date: Date) => boolean;
  onValidate?: OnValidateCallbackType;
  isComparison?: boolean;
  mainRangeDuration?: number;
  intl: IntlShape;
  hideButtons?: boolean;
}

export interface DateRangeState {
  draft: DateValue;
  valid: ValidState;
  activeDateField: ActiveDateField;
  setActiveDateField: (field: ActiveDateField) => void;
  calendarMonth: Date;
  hoverDate: Date | undefined;
  handleApply: () => void;
  handleCancel: () => void;
  handlePresetChange: (from: Date | undefined, to: Date | undefined, presetValue: string, labelKey: string) => void;
  handleDayClick: (proposedDate: Date) => void;
  handleDayHover: (date: Date) => void;
  handleCustomInputChange: (from: Date | undefined, to: Date | undefined) => void;
}

function getInitialMonth(from: Date | undefined, to: Date | undefined, today: Date): Date {
  let month = subMonths(today, 1);
  if (to && isValidDate(to)) month = subMonths(to, 1);
  if (from && isValidDate(from) && !isDateInView(from, month)) month = from;
  if (getMonth(month) === getMonth(today)) month = subMonths(month, 1);
  return month;
}

const makeValidationHandler =
  (
    setValid: (v: ValidState) => void,
    onValidate: OnValidateCallbackType | undefined,
    isComparison: boolean,
    _intl: IntlShape
  ) =>
  ({ from, to }: { from: Date; to: Date }) => {
    setValid({ from: '', to: '', type: '' });
    if (!onValidate) return;
    const result = onValidate(from, to, isComparison, false);
    setValid({
      from: result.startDateHelperText ?? '',
      to: result.endDateHelperText ?? '',
      type: result.error ? 'error' : '',
    });
  };

export function useDateRangeState({
  value,
  onChange,
  onCancel,
  today,
  disabledDates,
  onValidate,
  isComparison = false,
  mainRangeDuration,
  intl,
  hideButtons = false,
}: DateRangeStateOptions): DateRangeState {
  const [draft, setDraft] = useState(value);
  const [valid, setValid] = useState<ValidState>({ from: '', to: '', type: '' });
  const [activeDateField, setActiveDateField] = useState<ActiveDateField>({ range: 'range', field: 'start' });
  const [calendarMonth, setCalendarMonth] = useState(() => getInitialMonth(draft.range.from, draft.range.to, today));
  const [hoverDate, setHoverDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    setCalendarMonth(getInitialMonth(draft.range.from, draft.range.to, today));
  }, [draft.range.from, draft.range.to, today]);

  const handleApply = useCallback(() => {
    if (!isValidDate(draft.range.from) || !isValidDate(draft.range.to)) {
      setValid(prev => ({ ...prev, type: 'error' }));
      return;
    }
    if (!valid.from && !valid.to) {
      onChange(draft);
    } else {
      setValid(prev => ({ ...prev, type: 'error' }));
    }
  }, [draft, valid, onChange]);

  const handleCancel = useCallback(() => {
    setDraft(value);
    onCancel();
  }, [value, onCancel]);

  const handlePresetChange = useCallback(
    (from: Date | undefined, to: Date | undefined, presetValue: string, labelKey: string) => {
      const presetLabel = presetValue !== 'custom' ? labelKey : undefined;
      const newDraft = { ...draft, range: { from, to, presetValue, presetLabel } };
      setDraft(newDraft);
      if (hideButtons && presetValue !== 'custom') {
        onChange(newDraft);
      }
    },
    [draft, hideButtons, onChange]
  );

  const handleDayClick = useCallback(
    (proposedDate: Date) => {
      if (isComparison) {
        const duration = mainRangeDuration ?? 0;
        let newFrom = proposedDate;
        let newTo = addDays(newFrom, duration);

        if (disabledDates(newTo)) {
          let candidate = newFrom;
          for (let i = 0; i < duration + 1; i++) {
            candidate = addDays(candidate, -1);
            if (!disabledDates(addDays(candidate, duration))) break;
          }
          newFrom = candidate;
          newTo = addDays(newFrom, duration);
        }

        setDraft(prev => ({
          ...prev,
          range: { ...prev.range, from: newFrom, to: newTo, presetValue: 'custom', presetLabel: undefined },
        }));
        return;
      }

      const currentRange = { ...draft.range };
      const [nextActive, nextRange] = getNextState(activeDateField, currentRange, '', proposedDate);

      if (nextActive.field === 'start') {
        setHoverDate(undefined);
      }

      setDraft(prev => ({
        ...prev,
        range: { ...nextRange, presetValue: 'custom', presetLabel: undefined },
      }));
      setActiveDateField(nextActive);
    },
    [isComparison, mainRangeDuration, disabledDates, draft.range, activeDateField]
  );

  const handleDayHover = useCallback(
    (date: Date) => {
      if (!isComparison && activeDateField.field === 'end' && isValidDate(draft.range.from)) {
        setHoverDate(date);
      } else {
        setHoverDate(undefined);
      }
    },
    [activeDateField.field, draft.range.from, isComparison]
  );

  const handleCustomInputChange = useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      if (isComparison && isValidDate(from)) {
        const duration = mainRangeDuration ?? 0;
        let newFrom = from;
        let newTo = addDays(newFrom, duration);
        if (disabledDates(newTo)) {
          let candidate = newFrom;
          for (let i = 0; i < duration + 1; i++) {
            candidate = addDays(candidate, -1);
            if (!disabledDates(addDays(candidate, duration))) break;
          }
          newFrom = candidate;
          newTo = addDays(newFrom, duration);
        }
        setDraft(prev => ({
          ...prev,
          range: { ...prev.range, from: newFrom, to: newTo, presetValue: 'custom', presetLabel: undefined },
        }));
        return;
      }
      setDraft(prev => ({
        ...prev,
        range: { ...prev.range, from, to, presetValue: 'custom', presetLabel: undefined },
      }));
    },
    [isComparison, mainRangeDuration, disabledDates]
  );

  const handleValidate = useCallback(makeValidationHandler(setValid, onValidate, isComparison, intl), [
    onValidate,
    intl,
    isComparison,
  ]);

  useEffect(() => {
    if (draft.range.from && draft.range.to) {
      handleValidate({ from: draft.range.from, to: draft.range.to });
    } else {
      setValid({ from: '', to: '', type: '' });
    }
  }, [draft.range.from, draft.range.to, handleValidate]);

  return {
    draft,
    valid,
    activeDateField,
    setActiveDateField,
    calendarMonth,
    hoverDate,
    handleApply,
    handleCancel,
    handlePresetChange,
    handleDayClick,
    handleDayHover,
    handleCustomInputChange,
  };
}
